import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, Input, OnInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { PrivateService } from 'app/core/services/private.service';

interface InsertionResult {
  employeeId: string;
  employeeName: string;
  status: 'success' | 'failed' | 'skipped';
  error?: string;
  C_BPartner_ID?: number;
}

interface BatchResponse {
  totalProcessed: number;
  totalSuccess: number;
  totalSkipped: number;
  totalFailed: number;
  success: InsertionResult[];
  failed: InsertionResult[];  // includes both 'failed' and 'skipped'
}

@Component({
  selector: 'modal-import-employee',
  templateUrl: './modal-import-employee.component.html',
  styleUrls: ['./modal-import-employee.component.scss']
})
export class ModalImportEmployeeComponent implements OnInit, AfterViewInit {

  isLoading = false;
  formatError = false;
  employeeIdError = false;
  displayTable = true;
  isImported = false;

  insertionMessage = '';
  activeTab: 'all' | 'success' | 'skipped' | 'failed' = 'all';

  summaryStats: Omit<BatchResponse, 'success' | 'failed'> = {
    totalProcessed: 0,
    totalSuccess: 0,
    totalSkipped: 0,
    totalFailed: 0,
  };

  allResults: InsertionResult[] = [];
  invalidEmployeeIds: string[] = [];

  @Input() importedData: any[] = [];
  selectedEmployees: any[] = [];

  dataSource = new MatTableDataSource<any>();
  dataSource1 = new MatTableDataSource<InsertionResult>();

  displayedColumns1: string[] = ['employeeId', 'employeeName', 'status', 'error'];

  displayedColumns: string[] = [
    'select', 'employeeId', 'username', 'firstName', 'lastName',
    'department', 'branch', 'payroll', 'payrollJob',
    'startDate', 'endDate',
    'sssNumber', 'philhealthNumber', 'pagibigNumber', 'tinNumber',
    'paymentRule', 'unpaidLunchBreak', 'unpaidCoffeeBreak',
    'gracePeriod', 'firstTimeInBuffer', 'lastTimeInBuffer',
    'maxOvertime', 'regularHours',
    'totalVacation', 'totalSick', 'availableVacation', 'availableSick',
    'streetAddress', 'country', 'region', 'province', 'city', 'postal', 'phone'
  ];

  expectedHeaders: string[] = [
    'employeeId', 'username', 'firstName', 'lastName',
    'department', 'branch', 'payroll', 'payrollJob',
    'startDate', 'endDate',
    'sssNumber', 'philhealthNumber', 'pagibigNumber', 'tinNumber',
    'paymentRule', 'unpaidLunchBreak', 'unpaidCoffeeBreak',
    'gracePeriod', 'firstTimeInBuffer', 'lastTimeInBuffer',
    'maxOvertime', 'regularHours',
    'totalVacation', 'totalSick', 'availableVacation', 'availableSick',
    'streetAddress', 'country', 'region', 'province', 'city', 'postal', 'phone'
  ];

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild('paginator1') paginator1!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    public activeModal: NgbActiveModal,
    private http: HttpClient,
    private privateService: PrivateService
  ) { }

  ngOnInit(): void {
    this.dataSource = new MatTableDataSource(this.importedData);
    this.selectedEmployees = [...this.importedData];

    if (!this.isDataFormatCorrect()) {
      this.formatError = true;
    }

    this.invalidEmployeeIds = this.importedData
      .filter(emp => this.isInvalidEmployeeId(emp.employeeId))
      .map(emp => emp.employeeId);

    this.employeeIdError = this.invalidEmployeeIds.length > 0;
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  // ── Validation ──────────────────────────────────────────────────────────

  isDataFormatCorrect(): boolean {
    if (!this.importedData.length) return true;
    const importedHeaders = Object.keys(this.importedData[0]);
    return this.expectedHeaders.every(h => importedHeaders.includes(h));
  }

  isInvalidEmployeeId(str: string): boolean {
    if (!str) return false;
    return /[^a-zA-Z0-9_]/.test(str);
  }

  // ── Selection ────────────────────────────────────────────────────────────

  onCheckboxChange(employee: any, isChecked: boolean): void {
    if (isChecked) {
      if (!this.isSelected(employee)) this.selectedEmployees.push(employee);
    } else {
      this.selectedEmployees = this.selectedEmployees.filter(e => e !== employee);
    }
  }

  isSelected(employee: any): boolean {
    return this.selectedEmployees.includes(employee);
  }

  toggleSelectAll(isChecked: boolean): void {
    this.selectedEmployees = isChecked ? [...this.importedData] : [];
  }

  // ── Tab Filtering ────────────────────────────────────────────────────────

  switchTab(tab: 'all' | 'success' | 'skipped' | 'failed'): void {
    this.activeTab = tab;
    const filtered = tab === 'all'
      ? this.allResults
      : this.allResults.filter(r => r.status === tab);
    this.dataSource1.data = filtered;
    this.dataSource1.paginator = this.paginator1;
  }

  // ── Save ─────────────────────────────────────────────────────────────────

  saveSelectedEmployees(): void {
    if (!this.selectedEmployees.length) return;

    this.isLoading = true;
    this.displayTable = false;
    this.isImported = false;
    this.allResults = [];
    this.insertionMessage = 'Please wait...';

    const toInt = (val: any): number => {
      const n = +val;
      return isNaN(n) ? 0 : n;
    };

    const convertedEmployees = this.selectedEmployees.map(employee => ({
      ...employee,
      firstTimeInBuffer: toInt(employee.firstTimeInBuffer),
      gracePeriod: toInt(employee.gracePeriod),
      lastTimeInBuffer: toInt(employee.lastTimeInBuffer),
      maxOvertime: toInt(employee.maxOvertime),
      regularHours: toInt(employee.regularHours),
      unpaidCoffeeBreak: toInt(employee.unpaidCoffeeBreak),
      unpaidLunchBreak: toInt(employee.unpaidLunchBreak),
      totalVacationLeave: toInt(employee.totalVacation),      // ← was totalVacation
      totalSickLeave: toInt(employee.totalSick),          // ← was totalSick
      availableVacationLeave: toInt(employee.availableVacation),  // ← was availableVacation
      availableSickLeave: toInt(employee.availableSick),      // ← was availableSick
      startDate: this.formatDate(employee.startDate),
      endDate: this.formatDate(employee.endDate),
    }));

    this.privateService.insertEmployeeDetailsFromCSV(convertedEmployees).subscribe({
      next: (response: BatchResponse) => {
        // Flatten success + failed into one array for the results table
        this.allResults = [
          ...response.success,
          ...response.failed
        ];

        this.summaryStats = {
          totalProcessed: response.totalProcessed,
          totalSuccess: response.totalSuccess,
          totalSkipped: response.totalSkipped,
          totalFailed: response.totalFailed,
        };

        this.dataSource1 = new MatTableDataSource(this.allResults);
        // Bind paginator after view settles
        setTimeout(() => { this.dataSource1.paginator = this.paginator1; });

        this.insertionMessage = this.buildSummaryMessage(response);
        this.isLoading = false;
        this.isImported = true;
        this.displayTable = true;
        this.activeTab = 'all';
      },
      error: (err) => {
        console.error('Error inserting employees:', err);
        this.insertionMessage = 'An unexpected error occurred while importing employees.';
        this.isLoading = false;
        this.displayTable = true;
      }
    });
  }

  // ── Helpers ──────────────────────────────────────────────────────────────

  private buildSummaryMessage(r: BatchResponse): string {
    if (r.totalFailed === 0 && r.totalSkipped === 0) {
      return `All ${r.totalSuccess} employee(s) imported successfully.`;
    }
    const parts: string[] = [];
    if (r.totalSuccess > 0) parts.push(`${r.totalSuccess} imported`);
    if (r.totalSkipped > 0) parts.push(`${r.totalSkipped} skipped (already exist)`);
    if (r.totalFailed > 0) parts.push(`${r.totalFailed} failed`);
    return parts.join(', ') + '.';
  }

  private formatDate(dateString: string): string {
    if (dateString && dateString.length === 16) {
      const complete = dateString + ':00';
      const [datePart, timePart] = complete.split(' ');
      const [day, month, year] = datePart.split('/').map(Number);
      const [hour, minute, second] = timePart.split(':').map(Number);
      return new Date(Date.UTC(year, month - 1, day, hour, minute, second)).toISOString();
    }
    return dateString;
  }

  downloadTemplate(): void {
    this.http.get('assets/csv/Employee_Data_Template.csv', { responseType: 'blob' }).subscribe((data: Blob) => {
      const url = window.URL.createObjectURL(new Blob([data], { type: 'text/csv' }));
      const a = Object.assign(document.createElement('a'), { href: url, download: 'Employee_Data_Template.csv' });
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    });
  }
}